import type { ReactNode } from "react";
import type { Detail } from "../snak/SnakContext";
import { Ref } from "../components/Ref";
import { Tag } from "../components/Tag";
import { Placeholder } from "../components/Placeholder";
import { MINT_LIMITATIONS } from "../data/limitations";
export const studyDesignDetail: Detail = {
  kicker: "MINT · Study design",
  title: "A German multicentre real-world study",
  body: <>
      <Tag kind="evidence" data-id="element-126" data-mp="30jnp0p0xw7kv" />
      <Rows rows={[["Design", <>Real-world, observational, non-randomised, multicentre<Ref n={1} data-id="element-128" data-mp="35ih4gmzse8no" /></>], ["Setting", "18 centers in Germany"], ["Patients", <>109 patients with mycosis fungoides (MF) or Sézary syndrome (SS) treated with the product <Placeholder data-id="element-129" data-mp="2vs5ou36qqb0y">inclusion criteria, MF/SS split, stage and prior-therapy distribution</Placeholder></>], ["Endpoints reported", "Best overall response rate · median progression-free survival · median time to next treatment"], ["Data collection", <Placeholder data-id="element-130" data-mp="2thu1l0q1ehne">retrospective / prospective documentation, observation period, data cut-off</Placeholder>], ["Response criteria", <Placeholder data-id="element-131" data-mp="2ok1xz9g1573d">e.g. global response per ISCL/EORTC/USCLC consensus — confirm from final materials</Placeholder>]]} data-id="element-127" data-mp="30kfk7yqmk3am" />
      <Limitations data-id="element-132" data-mp="32ci78rziy1cq" />
    </>
};
export const pfsDetail: Detail = {
  kicker: "MINT · Endpoint",
  title: "Median progression-free survival: 20.2 months",
  body: <>
      <Tag kind="evidence" data-id="element-133" data-mp="3c70ajw113uw0" />
      <Section title="Definition" data-id="element-134" data-mp="3caeev9t96e63">
        Time from start of treatment to documented disease progression or death from any cause.{" "}
        <Placeholder data-id="element-135" data-mp="3l9jdeg4te7i9">confirm exact MINT endpoint definition</Placeholder>
      </Section>
      <Section title="Study context" data-id="element-136" data-mp="3h74u5ux8h5qg">
        Reported in 109 patients across 18 German centers in the final MINT results.<Ref n={1} data-id="element-137" data-mp="1qugtcxyjbhmq" /> Median value; 95% CI{" "}
        <Placeholder data-id="element-138" data-mp="1qxbd0n9q4yf3">insert</Placeholder>; median follow-up <Placeholder data-id="element-139" data-mp="1muf27iqfe6lp">insert</Placeholder>.
      </Section>
      <Section title="How to read it" data-id="element-140" data-mp="1mvzwd78tvnj1">
        A median from a single-arm, observational cohort describes what was observed in routine care. It is not a
        comparison with any other treatment or with clinical-trial results.
      </Section>
      <Limitations data-id="element-141" data-mp="3vvjgue0rbb5u" />
    </>
};
export const ttntDetail: Detail = {
  kicker: "MINT · Endpoint",
  title: "Median time to next treatment: 14.9 months",
  body: <>
      <Tag kind="evidence" data-id="element-142" data-mp="8s4vo0twjser" />
      <Section title="Definition" data-id="element-143" data-mp="8qtwyffbdaui">
        Time from start of treatment to initiation of the next systemic therapy.{" "}
        <Placeholder data-id="element-144" data-mp="dpdem3mvk0h2">confirm exact MINT endpoint definition, including handling of death</Placeholder>
      </Section>
      <Section title="Study context" data-id="element-145" data-mp="ijrfumo7x1tn">
        Reported in 109 patients across 18 German centers in the final MINT results.<Ref n={1} data-id="element-146" data-mp="sfujjwypx8ip" /> 95% CI{" "}
        <Placeholder data-id="element-147" data-mp="sgniibrfqtqa">insert</Placeholder>.
      </Section>
      <Section title="How to read it" data-id="element-148" data-mp="se1ayc4joe82">
        Time to next treatment reflects real-world treatment decisions. It is influenced by disease course, tolerability,
        patient preference and physician judgement, and is not a measure of efficacy alone.
      </Section>
      <Limitations data-id="element-149" data-mp="2cwbu44q1i49r" />
    </>
};
export const mintResultsDetail: Detail = {
  kicker: "MINT · Final results",
  title: "What the MINT study reported",
  body: <>
      <Tag kind="evidence" data-id="element-150" data-mp="230ifmfsrg0pc" />
      <div className="mt-4 divide-y divide-rule border-y border-rule" data-id="element-151" data-mp="231a6xefbrc9t">
        {[["62.4%", "Best overall response rate"], ["20.2 mo", "Median progression-free survival"], ["14.9 mo", "Median time to next treatment"], ["109 / 18", "Patients / German centers"]].map(([v, l]) => <div key={l} className="flex items-baseline justify-between gap-4 py-3" data-id="element-152" data-mp="2lyl2op1qznki">
            <span className="text-[13px] text-charcoal" data-id="element-153" data-mp="2lwgzyafl4ult">{l}</span>
            <span className="tabular text-[18px] font-light text-ink" data-id="element-154" data-mp="3jeqsfinqavns">{v}</span>
          </div>)}
      </div>
      <p className="mt-3 text-[12px] text-muted" data-id="element-155" data-mp="3elxouy3zxtda">
        Source: final MINT results announcement, October 2026.<Ref n={1} data-id="element-156" data-mp="2z3xrczqrmzbw" /> Provisional figures, subject to
        verification against the full final study materials.
      </p>
      <Section title="Further detail" data-id="element-157" data-mp="2p71g4m9hfz3n">
        <Placeholder data-id="element-158" data-mp="2p834ttjf8k3u">full publication, subgroup analyses (only if reported), safety findings as reported in MINT</Placeholder>
      </Section>
      <Limitations data-id="element-159" data-mp="2u88pgpr9x6cf" />
    </>
};
export const clinicalEvidenceDetail: Detail = {
  kicker: "Clinical evidence",
  title: "Three sources, three kinds of evidence",
  body: <>
      <p data-id="element-160" data-mp="3mu0gb6zdvp6a">Each source answers a different question and should be read on its own terms.</p>
      <div className="mt-5 space-y-6" data-id="element-161" data-mp="385e2xatbh7hp">
        <div data-id="element-162" data-mp="3d50gjld24pr1">
          <Tag kind="trial" data-id="element-163" data-mp="3d4ql3i98tqdy" />
          <p className="mt-2" data-id="element-164" data-mp="3d3yu6kqlcgu0">
            Randomised, open-label, international phase 3 trial of the product versus vorinostat in 372 adults with
            previously treated MF or SS. Primary endpoint: investigator-assessed progression-free survival. Response was
            also reported by disease compartment (skin, blood, lymph nodes, viscera).<Ref n={2} data-id="element-165" data-mp="1u3zhph8yk6fk" />
          </p>
        </div>
        <div data-id="element-166" data-mp="1p66fezarzwkk">
          <Tag kind="evidence" data-id="element-167" data-mp="1p6pwyewikiy2" />
          <p className="mt-2" data-id="element-168" data-mp="1p5es0brc155d">
            Real-world, observational, non-randomised data from 109 patients at 18 German centers, describing outcomes in
            routine care.<Ref n={1} data-id="element-169" data-mp="1yxkkrnperqlm" /> Not designed for comparison with MAVORIC or with other therapies.
          </p>
        </div>
        <div data-id="element-170" data-mp="1b9kibqiqibr7">
          <Tag kind="guidance" data-id="element-171" data-mp="1b9b0wrbr9cx4" />
          <p className="mt-2" data-id="element-172" data-mp="16956x4i0jebv">
            Expert recommendations on the use of the product in MF/SS in Germany.<Ref n={3} data-id="element-173" data-mp="1k8emc1yea4es" />{" "}
            <Placeholder data-id="element-174" data-mp="1k9pqw3znzg6y">insert verified key recommendations with page references</Placeholder>
          </p>
        </div>
      </div>
    </>
};
function Section({
  title,
  children
}: {
  title: string;
  children: ReactNode;
}) {
  return <section className="mt-5" data-id="element-175" data-mp="2qo8kcvbfonx6">
      <h3 className="text-[12px] font-semibold text-ink" data-id="element-176" data-mp="2qp01x2874l4g">{title}</h3>
      <div className="mt-1.5" data-id="element-177" data-mp="1oasrw4n6f1mj">{children}</div>
    </section>;
}
function Rows({
  rows
}: {
  rows: [string, ReactNode][];
}) {
  return <dl className="mt-4 divide-y divide-rule border-y border-rule" data-id="element-178" data-mp="1t8ubqrsff4z2">
      {rows.map(([k, v]) => <div key={k} className="grid grid-cols-[7.5rem_1fr] gap-3 py-2.5 text-[13px]" data-id="element-179" data-mp="1xcjzjf1a1ij1">
          <dt className="font-medium text-ink" data-id="element-180" data-mp="22atv3qqwyl56">{k}</dt>
          <dd data-id="element-181" data-mp="2287mrow781lw">{v}</dd>
        </div>)}
    </dl>;
}
function Limitations() {
  return <section className="mt-6 rounded-lg bg-wash p-4" data-id="element-182" data-mp="15i8594y2lsr6">
      <h3 className="text-[12px] font-semibold text-ink" data-id="element-183" data-mp="19my0crkwn3b1">Limitations</h3>
      <ul className="mt-2 space-y-1.5 text-[13px]" data-id="element-184" data-mp="1el98mxvjx5r5">
        {MINT_LIMITATIONS.map(l => <li key={l} className="flex gap-2" data-id="element-185" data-mp="1jbfvas6i6g6r">
            <span className="mt-[9px] h-px w-3 shrink-0 bg-charcoal" data-id="element-186" data-mp="1jchghph8gj27" />
            <span data-id="element-187" data-mp="lx3nia0l5eqs">{l}</span>
          </li>)}
      </ul>
    </section>;
}